/**
 * Roadrunner brand mark + shared palette for the illustration set.
 *
 * Drawn in a 64×64 box, facing right, mid-stride with the tail held up.
 * Colors reference the design tokens with the hex as a fallback so the
 * artwork renders the same outside the app (tests, rasterizing).
 */

export const palette = {
  ink: 'var(--ink, #3b3128)',
  inkSoft: 'var(--ink-soft, #6b5e4e)',
  card: 'var(--card, #fbf7ee)',
  paper: 'var(--paper, #f6efe0)',
  board: 'var(--board, #e8dcc4)',
  cardEdge: 'var(--card-edge, #e2d5bb)',
  forest: 'var(--forest, #3f5b45)',
  sage: 'var(--sage, #8ea283)',
  sageSoft: 'var(--sage-soft, #d7dfc9)',
  sky: 'var(--sky, #587b96)',
  skySoft: 'var(--sky-soft, #d6e1e8)',
  adobe: 'var(--adobe, #a4503a)',
  mustard: 'var(--mustard, #d4a03a)',
  mustardSoft: 'var(--mustard-soft, #f3e2b6)',
  dusk: 'var(--dusk, #7a6a8f)',
  // Bird plumage (not theme tokens)
  body: '#8a6a4a',
  bodyDark: '#6f5238',
  cream: '#efe3c8',
} as const;

const P = palette;

/** Accessible-name helper shared by the illustration components. */
export function a11yProps(title?: string) {
  return title ? ({ role: 'img' } as const) : ({ 'aria-hidden': true } as const);
}

type FigureProps = {
  /** SVG transform applied to the 64×64 figure. */
  transform?: string;
  /** Extra plumage streaks; off for the small brand mark. */
  detailed?: boolean;
};

/** The standing / striding roadrunner, as a <g> in a 64×64 coordinate box. */
export function RoadrunnerFigure({ transform, detailed = false }: FigureProps) {
  const line = {
    stroke: P.ink,
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
  const tail = 'M23.45 29.6 L9.05 11.11 C6.99 8.93 0.89 14.71 2.95 16.89 L20.55 32.4 Z';
  const body =
    'M50.4 18.3 C51.4 16.9 51.2 14.6 50.2 13.4 C49.4 10.4 45.8 9.4 43 10.2 Q40 7.8 36.6 6.8 Q38.8 9.4 40.2 11.4 Q37.4 10.2 34.2 10.6 Q37.2 12.2 39.6 14 Q37.6 14.4 35.4 15.6 Q38.4 15.8 40.6 16.6 C40 20 38.8 23.4 36.5 25.2 C32 28.6 26 30.6 21 32 C18.6 32.8 18.6 35.6 21 36.8 C26 40.4 35.5 40.6 41.5 37 C45.5 34.6 47.4 30 47.4 26 C47.4 22.4 48.6 19.6 50.4 18.3 Z';
  return (
    <g transform={transform}>
      {/* legs */}
      <g fill="none" {...line} strokeWidth={2.2}>
        <path d="M29.5 38.8 L24.6 47 L21.4 55.6 M21.4 55.6 L25.4 56.4 M21.4 55.6 L19.2 57.4" />
        <path d="M34 39.4 L34.6 47.6 L40.4 56.2 M40.4 56.2 L44.8 56.4 M40.4 56.2 L37.8 57.4" />
      </g>
      {/* tail, tucked behind the body, pale feather tips */}
      <path d={tail} fill={P.bodyDark} />
      <path d="M10.89 13.48 L9.05 11.11 C6.99 8.93 0.89 14.71 2.95 16.89 L5.2 18.87 Z" fill={P.cream} />
      <path d="M18.4 27.8 L5.2 13.4" stroke={P.ink} strokeWidth={0.9} strokeLinecap="round" />
      <path d={tail} fill="none" {...line} />
      {/* beak */}
      <path d="M49.6 13.8 Q56.5 13.9 62.6 17.8 Q56.5 18.3 49.6 18.2 Z" fill={P.inkSoft} {...line} />
      {/* body, neck, head */}
      <path d={body} fill={P.body} />
      {/* pale throat and belly */}
      <path
        d="M50.2 18.4 C48.6 19.6 47.4 22.4 47.4 26 C47.4 30 45.5 34.6 41.5 37 C35.5 40.6 26 40.4 21 36.8 C27 37.4 35 36.4 40 32.6 C43.4 30 44.8 26 45.4 22 C45.8 20 47.4 18.8 50.2 18.4 Z"
        fill={P.cream}
      />
      {/* crest cap */}
      <path
        d="M50 12.8 C49.4 10.4 45.8 9.4 43 10.2 Q40 7.8 36.6 6.8 Q38.8 9.4 40.2 11.4 Q37.4 10.2 34.2 10.6 Q37.2 12.2 39.6 14 Q37.6 14.4 35.4 15.6 Q38.4 15.8 40.6 16.6 C42 14.4 44.4 12.6 47.4 12.4 Z"
        fill={P.bodyDark}
      />
      {/* wing with pale-edged feathers */}
      <path
        d="M38 25.6 C33 29.4 27 31.6 21.4 33 C26.5 35.6 35 35.6 40.6 31.8 C40.6 29.2 39.6 27 38 25.6 Z"
        fill={P.bodyDark}
      />
      <g fill="none" stroke={P.cream} strokeWidth={1.3} strokeLinecap="round">
        <path d="M37.4 29.2 l-2.2 1.3 M32.4 31.7 l-2.4 1" />
        {detailed && <path d="M29.6 30.2 l-2 0.8 M27.6 33 l-2.2 0.5" />}
      </g>
      {detailed && (
        <g fill="none" stroke={P.body} strokeWidth={1.1} strokeLinecap="round">
          <path d="M47.8 21.6 L46.8 23.6 M46.8 26.4 L45.6 28.6 M45 31 L43.6 33" />
        </g>
      )}
      <path d={body} fill="none" {...line} />
      {/* bare skin behind the eye: blue fading to orange */}
      <path d="M43.2 14.4 C42 14.2 40.9 14.8 40.3 15.8 C41.2 16.3 42.3 16.3 43.2 15.9 Z" fill={P.adobe} />
      <path d="M45.4 13.9 C44.2 13.7 43 14.2 42.4 15.4 C43.4 16 44.6 16 45.6 15.4 Z" fill={P.sky} />
      {/* eye */}
      <circle cx={46.6} cy={14.4} r={1.5} fill={P.ink} />
      <circle cx={47.1} cy={13.9} r={0.45} fill={P.cream} />
    </g>
  );
}

type MarkProps = { size?: number; title?: string; className?: string };

/** Brand mark: the roadrunner in profile. Reads cleanly from ~28px up. */
export function RoadrunnerMark({ size = 40, title, className }: MarkProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      {...a11yProps(title)}
    >
      {title && <title>{title}</title>}
      <RoadrunnerFigure />
    </svg>
  );
}
